'use client'

import { type NormalizedError, normalizeError } from 'korvo-app-normalize-error'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'

import { type ShellConfig } from '../../config/shell-config'

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

export type RegisterFormProps = {
  /** Shell configuration (provides routes and branding) */
  config: ShellConfig
  /** Override redirect URL if needed */
  redirectUrl?: string
  /** Custom CSS classes for container */
  className?: string
}

export function RegisterForm({
  config,
  redirectUrl,
  className,
}: RegisterFormProps) {
  const finalRedirectUrl = redirectUrl || config.routes.signIn
  const searchParams = useSearchParams()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const initialRegistrationCode = ((v) => v !== null && v !== undefined ? v : '')(searchParams?.get('code'))
  const [registrationCode, setRegistrationCode] = useState(initialRegistrationCode)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const router = useRouter()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    
    // Basic validation
    if (!email || !password) {
      setError('Please fill in all required fields')
      setIsLoading(false)
      return
    }
    
    try {
      // Use Better Auth registration code endpoint
      const response = await fetch('/api/auth/sign-up-with-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name: name || undefined,
          registrationCode: registrationCode || undefined,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Better Auth APIError format: { message: string }
        const errorMessage = data.message || data.error || 'Registration failed. Please try again.'
        setError(errorMessage)
        setIsLoading(false)
        return
      }

      // Registration successful - Better Auth creates session automatically.
      // To land on signin page as unauthenticated (for "Account successfully created" UX),
      // sign out before redirecting. Use fetch so cookies are sent.
      await fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include' })
      await new Promise(resolve => setTimeout(resolve, 100))
      window.location.href = finalRedirectUrl
    } catch (err: unknown) {
      const errNormalized: NormalizedError = normalizeError(err)
      console.error('[Register] Registration exception:', errNormalized)
      setError('An error occurred during registration. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <div
      className={cn(
        'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8',
        className
      )}
      style={{ 
        paddingBottom: 'calc(var(--footer-height, 5rem) + 3rem)',
      }}
    >
      <div className="min-h-[60vh] flex items-center justify-center py-12">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold" style={{ color: 'var(--foreground-default)' }}>
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--foreground-muted)' }}>
            {config.branding.brandingText || `Register for ${config.branding.appName}`}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={(e) => void submit(e)}>
          {error && (
            <div className="px-4 py-3 rounded-md border" style={{
              backgroundColor: 'var(--danger-default)',
              borderColor: 'var(--danger-default)',
              color: 'var(--danger-foreground)',
            }}>
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium" style={{ color: 'var(--label-foreground)' }}>
                Name
              </label>
              <input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                style={{
                  borderColor: 'var(--input-border)',
                  backgroundColor: 'var(--input-background)',
                  color: 'var(--input-foreground)',
                }}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'var(--label-foreground)' }}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                style={{
                  borderColor: 'var(--input-border)',
                  backgroundColor: 'var(--input-background)',
                  color: 'var(--input-foreground)',
                }}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--label-foreground)' }}>
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                style={{
                  borderColor: 'var(--input-border)',
                  backgroundColor: 'var(--input-background)',
                  color: 'var(--input-foreground)',
                }}
              />
            </div>
            <div>
              <label htmlFor="registrationCode" className="block text-sm font-medium" style={{ color: 'var(--label-foreground)' }}>
                Registration code
              </label>
              <input
                id="registrationCode"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
                placeholder="Required if invite-only is enabled"
                className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                style={{
                  borderColor: 'var(--input-border)',
                  backgroundColor: 'var(--input-background)',
                  color: 'var(--input-foreground)',
                }}
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full py-3 px-4 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors'
              )}
              style={{
                backgroundColor: 'var(--primary-default)',
                color: 'var(--primary-foreground)',
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'var(--primary-hover)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--primary-default)';
              }}
            >
              {isLoading ? 'Creating...' : 'Create Account'}
            </button>
          </div>
          <div className="text-center">
            <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
              Already have an account?{' '}
              <Link href={config.routes.signIn} className="hover:underline" style={{ color: 'var(--link-default)' }}>
                Sign in
              </Link>
            </p>
          </div>
        </form>
        </div>
      </div>
    </div>
  )
}

